import { useState, useEffect } from 'react';
import { Plus, Trash, Move, Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { ProductRecommendationEditor } from './ProductRecommendationEditor';

interface PackingItem {
  id: string;
  name: string;
  category?: string;
  is_essential: boolean;
}

interface ProductRecommendation {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  price?: number;
  currency?: string;
  affiliate_link: string;
  category?: string;
}

export interface PackingList {
  id?: string;
  title: string;
  description?: string;
  destination_type?: string;
  items: PackingItem[];
  products: ProductRecommendation[];
}

interface PackingListEditorProps {
  blogPostId?: string;
  onSave?: (packingListId: string) => void;
  existingPackingListId?: string;
}

export const PackingListEditor = ({ 
  blogPostId, 
  onSave, 
  existingPackingListId 
}: PackingListEditorProps) => {
  const [packingList, setPackingList] = useState<PackingList>({
    title: '',
    description: '',
    destination_type: 'Beach',
    items: [],
    products: []
  });
  const [loading, setLoading] = useState(false);
  const [isNewItem, setIsNewItem] = useState(true);
  const [currentItem, setCurrentItem] = useState<PackingItem>({
    id: uuidv4(),
    name: '',
    category: 'Essentials',
    is_essential: true
  });
  const [isSaved, setIsSaved] = useState(false);
  const [existingProducts, setExistingProducts] = useState<ProductRecommendation[]>([]);
  const [isLoadingExistingProducts, setIsLoadingExistingProducts] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  useEffect(() => {
    const fetchPackingList = async () => {
      if (!existingPackingListId) return;
      
      setLoading(true);
      try {
        const { data: packingListData, error: packingListError } = await supabase
          .from('packing_lists')
          .select('*')
          .eq('id', existingPackingListId)
          .single();
          
        if (packingListError) throw packingListError;
        
        const { data: itemsData, error: itemsError } = await supabase
          .from('packing_list_items')
          .select('*')
          .eq('packing_list_id', existingPackingListId)
          .order('display_order', { ascending: true });
          
        if (itemsError) throw itemsError;
        
        const { data: productsData, error: productsError } = await supabase
          .from('product_recommendations')
          .select('*')
          .eq('packing_list_id', existingPackingListId)
          .order('display_order', { ascending: true });
          
        if (productsError) throw productsError;
        
        setPackingList({
          id: packingListData.id,
          title: packingListData.title,
          description: packingListData.description || '',
          destination_type: packingListData.destination_type || 'Beach',
          items: itemsData.map(item => ({
            id: item.id,
            name: item.name,
            category: item.category,
            is_essential: item.is_essential
          })),
          products: productsData.map(product => ({
            id: product.id,
            name: product.name,
            description: product.description || '',
            image_url: product.image_url || '',
            price: product.price,
            currency: product.currency || 'USD',
            affiliate_link: product.affiliate_link,
            category: product.category
          }))
        });
        setIsSaved(true);
      } catch (error) {
        console.error('Error fetching packing list:', error);
        toast.error('Failed to load packing list');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackingList();
  }, [existingPackingListId]);

  useEffect(() => {
    const fetchExistingProducts = async () => {
      setIsLoadingExistingProducts(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .order('name', { ascending: true });
          
        if (error) throw error;
        
        setExistingProducts(data.map(product => ({
          id: product.id,
          name: product.name,
          description: product.description || '',
          image_url: product.image_url || '',
          price: product.price,
          currency: product.currency || 'USD',
          affiliate_link: product.affiliate_link,
          category: product.category
        })));
      } catch (error) {
        console.error('Error fetching existing products:', error);
        toast.error('Failed to load existing products');
      } finally {
        setIsLoadingExistingProducts(false);
      }
    };
    
    fetchExistingProducts();
  }, []);

  const filteredExistingProducts = productSearchQuery 
    ? existingProducts.filter(product => 
        product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(productSearchQuery.toLowerCase())) ||
        (product.category && product.category.toLowerCase().includes(productSearchQuery.toLowerCase()))
      )
    : existingProducts;

  const handleAddItem = () => {
    if (!currentItem.name.trim()) {
      toast.error('Item name is required');
      return;
    }
    
    setPackingList(prev => ({
      ...prev,
      items: [...prev.items, { ...currentItem }]
    }));
    
    setCurrentItem({
      id: uuidv4(),
      name: '',
      category: 'Essentials',
      is_essential: true
    });
    
    setIsSaved(false);
  };

  const handleRemoveItem = (id: string) => {
    setPackingList(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
    setIsSaved(false);
  };

  const handleAddProduct = (product: ProductRecommendation) => {
    if (packingList.products.some(p => p.id === product.id)) {
      toast.info('This product is already in the packing list');
      return;
    }
    
    setPackingList(prev => ({
      ...prev,
      products: [...prev.products, product]
    }));
    setIsSaved(false);
  };

  const handleRemoveProduct = (id: string) => {
    setPackingList(prev => ({
      ...prev,
      products: prev.products.filter(product => product.id !== id)
    }));
    setIsSaved(false);
  };

  const handleUpdateProduct = (updatedProduct: ProductRecommendation) => {
    setPackingList(prev => ({
      ...prev,
      products: prev.products.map(product => 
        product.id === updatedProduct.id ? updatedProduct : product
      )
    }));
    setIsSaved(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPackingList(prev => ({ ...prev, [name]: value }));
    setIsSaved(false);
  };

  const handleSelectChange = (name: string, value: string) => {
    setPackingList(prev => ({ ...prev, [name]: value }));
    setIsSaved(false);
  };

  const handleCurrentItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  const handleAddExistingProduct = (product: ProductRecommendation) => {
    const newProduct = {
      ...product,
      id: uuidv4()
    };
    handleAddProduct(newProduct);
  };

  const handleSave = async () => {
    if (!packingList.title) {
      toast.error('Title is required');
      return;
    }
    
    setLoading(true);
    try {
      let packingListId = packingList.id;
      
      if (!packingListId) {
        const { data, error } = await supabase
          .from('packing_lists')
          .insert({
            title: packingList.title,
            description: packingList.description,
            destination_type: packingList.destination_type
          })
          .select();
          
        if (error) throw error;
        packingListId = data[0].id;
        
        setPackingList(prev => ({ ...prev, id: packingListId }));
      } else {
        const { error } = await supabase
          .from('packing_lists')
          .update({
            title: packingList.title,
            description: packingList.description,
            destination_type: packingList.destination_type,
            updated_at: new Date().toISOString()
          })
          .eq('id', packingListId);
          
        if (error) throw error;
      }
      
      if (blogPostId && !existingPackingListId) {
        const { error } = await supabase
          .from('blog_packing_list_links')
          .insert({
            blog_post_id: blogPostId,
            packing_list_id: packingListId
          });
          
        if (error) throw error;
      }
      
      if (existingPackingListId) {
        const { error } = await supabase
          .from('packing_list_items')
          .delete()
          .eq('packing_list_id', packingListId);
          
        if (error) throw error;
      }
      
      if (packingList.items.length > 0) {
        const { error } = await supabase
          .from('packing_list_items')
          .insert(packingList.items.map((item, index) => ({
            packing_list_id: packingListId,
            name: item.name,
            category: item.category,
            is_essential: item.is_essential,
            display_order: index
          })));
          
        if (error) throw error;
      }
      
      if (existingPackingListId) {
        const { error } = await supabase
          .from('product_recommendations')
          .delete()
          .eq('packing_list_id', packingListId);
          
        if (error) throw error;
      }
      
      if (packingList.products.length > 0) {
        const { error } = await supabase
          .from('product_recommendations')
          .insert(packingList.products.map((product, index) => ({
            packing_list_id: packingListId,
            name: product.name,
            description: product.description,
            image_url: product.image_url,
            price: product.price,
            currency: product.currency,
            affiliate_link: product.affiliate_link,
            category: product.category,
            display_order: index
          })));
          
        if (error) throw error;
      }
      
      toast.success('Packing list saved successfully');
      setIsSaved(true);
      
      if (onSave && packingListId) {
        onSave(packingListId);
      }
    } catch (error) {
      console.error('Error saving packing list:', error);
      toast.error('Failed to save packing list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>What to Pack</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              name="title"
              value={packingList.title}
              onChange={handleInputChange}
              placeholder="e.g., Beach Vacation Essentials"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={packingList.description}
              onChange={handleInputChange}
              placeholder="Brief description of this packing list"
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="destination-type">Destination Type</Label>
            <Select
              value={packingList.destination_type}
              onValueChange={(value) => handleSelectChange('destination_type', value)}
            >
              <SelectTrigger id="destination-type">
                <SelectValue placeholder="Select destination type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Beach">Beach</SelectItem>
                <SelectItem value="Mountain">Mountain</SelectItem>
                <SelectItem value="City">City</SelectItem>
                <SelectItem value="Camping">Camping</SelectItem>
                <SelectItem value="Cruise">Cruise</SelectItem>
                <SelectItem value="Safari">Safari</SelectItem>
                <SelectItem value="General">General</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Essential Items</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="item-name">Item Name</Label>
              <Input
                id="item-name"
                name="name"
                value={currentItem.name}
                onChange={handleCurrentItemChange}
                placeholder="e.g., Sunscreen"
              />
            </div>
            <div className="space-y-2 w-[150px]">
              <Label htmlFor="item-category">Category</Label>
              <Input
                id="item-category"
                name="category"
                value={currentItem.category}
                onChange={handleCurrentItemChange}
                placeholder="e.g., Essentials"
              />
            </div>
            <div className="flex items-center space-x-2 h-10 mt-8">
              <Checkbox
                id="is-essential"
                checked={currentItem.is_essential}
                onCheckedChange={(checked) => 
                  setCurrentItem(prev => ({ ...prev, is_essential: checked === true }))
                }
              />
              <Label htmlFor="is-essential">Essential</Label>
            </div>
            <Button type="button" onClick={handleAddItem} className="mt-8">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          
          {packingList.items.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              No items added yet. Add some essential items for travelers.
            </div>
          ) : (
            <div className="border rounded-md">
              <ul className="divide-y">
                {packingList.items.map((item, index) => (
                  <li key={item.id} className="flex justify-between items-center p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground text-sm">{index + 1}.</span>
                      <div>
                        <span className="font-medium">{item.name}</span>
                        {item.category && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            {item.category}
                          </span>
                        )}
                        {item.is_essential && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 rounded-full px-2 py-0.5 dark:bg-green-900 dark:text-green-100">
                            Essential
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Choose from Existing Products</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative w-full">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search existing products..."
              className="pl-9"
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
            />
          </div>
          
          {isLoadingExistingProducts ? (
            <div className="text-center py-4">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading products...</p>
            </div>
          ) : filteredExistingProducts.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              {productSearchQuery ? (
                <p>No products match your search</p>
              ) : (
                <p>No existing products found</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-60 overflow-y-auto p-1">
              {filteredExistingProducts.map(product => (
                <div key={product.id} className="border rounded-md p-3 flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-sm">{product.name}</h4>
                    {product.category && (
                      <p className="text-xs text-muted-foreground">{product.category}</p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleAddExistingProduct(product)}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <ProductRecommendationEditor
        products={packingList.products}
        onAddProduct={handleAddProduct}
        onRemoveProduct={handleRemoveProduct}
        onUpdateProduct={handleUpdateProduct}
      />
      
      <CardFooter className="flex justify-end pt-6">
        <Button 
          onClick={handleSave} 
          disabled={loading || isSaved}
          className="w-full sm:w-auto"
        >
          {loading ? (
            <>
              <div className="animate-spin h-4 w-4 mr-2 border-2 border-current border-t-transparent rounded-full"></div>
              Saving...
            </>
          ) : isSaved ? (
            <>
              <Save className="h-4 w-4 mr-2" /> Saved
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" /> Save Packing List
            </>
          )}
        </Button>
      </CardFooter>
    </div>
  );
};
